import { AnimatePresence, motion } from 'framer-motion'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectUserInformations } from '../../features/authSlice'
import { FiUser, FiBook, FiBookOpen, FiVideo, FiClock, FiCalendar } from 'react-icons/fi'
import Loader from '../Live/Loader'
import axios from 'axios'
import { API } from '../../shared/api/Api'
import StatItem from '../Statistics/StatItem'
import StatCard from '../Statistics/StatCard'

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.3 },
    },
}

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 100 },
    },
}

const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
}

const DashboardInstructor = () => {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [statistics, setStatistics] = useState({
        courses_count: 0,
        lessons_count: 0,
        live_sessions_count: 0,
        live_sessions: [],
        students_count: 0
    })
    const user = useSelector(selectUserInformations)

    // Memoize the fetch function to prevent unnecessary re-creations
    const fetchStatistics = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await axios.get(
                API.Dashboard.app_statistics_statsDataForInstructor,
                { headers: { Authorization: `Bearer ${user.token}` } }
            )

            if (data.status === 200) {
                setStatistics({
                    courses_count: data.data.courses_count,
                    lessons_count: data.data.lessons_count,
                    live_sessions_count: data.data.live_sessions_count,
                    live_sessions: data.data.live_sessions || [],
                    students_count: data.data.students_count || 0
                })
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load statistics')
            console.error('Error fetching instructor statistics:', err)
        } finally {
            setLoading(false)
        }
    }, [user?.token])

    useEffect(() => {
        if (user?.token) {
            fetchStatistics()
        }
    }, [user?.token, fetchStatistics])

    // Format date function
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB')
    }

    // Format time function
    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    // Memoize the sorted sessions to prevent re-sorting on each render
    const sortedSessions = useMemo(() => {
        return [...(statistics.live_sessions || [])].sort((a, b) => 
            new Date(b.started_at) - new Date(a.started_at)
        ).slice(0, 5) // Show only the 5 most recent sessions
    }, [statistics.live_sessions])

    // Handle refresh button click
    const handleRefresh = () => {
        fetchStatistics()
    }

    return (
        <div className="bg-[#EBEBEB] min-h-screen p-4 sm:p-6">
            <div className="max-w-full mx-auto">
                {/* Welcome Banner with Refresh Button */}
                <motion.div
                    variants={fadeIn}
                    initial="hidden"
                    animate="visible"
                    className="mb-6 sl:mb-8 border border-gray-300 rounded-lg p-4 xs:p-6 shadow-sm bg-gradient-to-l from-blue-100 to-[#134F98]"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 xs:gap-4">
                            <FiUser className="w-6 h-6 xs:w-8 xs:h-8 text-white" />
                            <div>
                                <h2 className="text-xl xs:text-2xl font-bold text-white mb-1 xs:mb-2">
                                    Welcome Back!
                                </h2>
                                <p className="text-xs xs:text-sm text-gray-100">
                                    {user?.gender === "male"
                                        ? `Mr. ${user?.name}`
                                        : `Ms. ${user?.name}`}
                                    , here's your teaching overview
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={handleRefresh}
                            className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                            aria-label="Refresh dashboard"
                            disabled={loading}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>
                </motion.div>

                <div>
                    {loading ? (
                        <AnimatePresence>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex justify-center items-center h-64"
                            >
                                <Loader />
                            </motion.div>
                        </AnimatePresence>
                    ) : error ? (
                        <AnimatePresence>
                            <motion.div
                                variants={fadeIn}
                                className="text-red-500 text-center p-4 bg-red-50 rounded-lg border border-red-100"
                            >
                                <p className="font-medium">{error}</p>
                                <button 
                                    onClick={handleRefresh} 
                                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                                >
                                    Try Again
                                </button>
                            </motion.div>
                        </AnimatePresence>
                    ) : (
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* Statistics Grid */}
                            <motion.div
                                className="grid grid-cols-1 md:grid-cols-2 sl:grid-cols-4 gap-4 sl:gap-6 mb-6 sl:mb-8"
                                variants={containerVariants}
                            >
                                <motion.div variants={cardVariants}>
                                    <StatCard
                                        title="Courses"
                                        value={statistics?.courses_count}
                                        icon={FiBook}
                                        bgColor="bg-gradient-to-b from-blue-100 to-blue-500"
                                        iconColor="bg-blue-500"
                                    >
                                        <StatItem
                                            label="Total Created"
                                            value={statistics.courses_count}
                                            color="text-blue-600"
                                        />
                                    </StatCard>
                                </motion.div>

                                <motion.div variants={cardVariants}>
                                    <StatCard
                                        title="Lessons"
                                        value={statistics?.lessons_count}
                                        icon={FiBookOpen}
                                        bgColor="bg-gradient-to-b from-purple-100 to-purple-500"
                                        iconColor="bg-purple-500"
                                    >
                                        <StatItem
                                            label="Total Created"
                                            value={statistics?.lessons_count}
                                            color="text-purple-600"
                                        />
                                    </StatCard>
                                </motion.div>

                                <motion.div variants={cardVariants}>
                                    <StatCard
                                        title="Live Sessions"
                                        value={statistics?.live_sessions_count}
                                        icon={FiVideo}
                                        bgColor="bg-gradient-to-b from-green-100 to-green-500"
                                        iconColor="bg-green-500"
                                    >
                                        <StatItem
                                            label="Total Conducted"
                                            value={statistics?.live_sessions_count}
                                            color="text-green-600"
                                        />
                                    </StatCard>
                                </motion.div>

                                <motion.div variants={cardVariants}>
                                    <StatCard
                                        title="Students"
                                        value={statistics?.students_count || 0}
                                        icon={FiCalendar}
                                        bgColor="bg-gradient-to-b from-amber-100 to-amber-500"
                                        iconColor="bg-amber-500"
                                    >
                                        <StatItem
                                            label="Total Enrolled"
                                            value={statistics?.students_count || 0}
                                            color="text-amber-600"
                                        />
                                    </StatCard>
                                </motion.div>
                            </motion.div>

                            {/* Live Sessions List */}
                            <motion.div
                                variants={cardVariants}
                                className="bg-white rounded-xl shadow-sm p-4 xs:p-6"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-base xs:text-lg font-semibold text-gray-800">
                                        Recent Live Sessions
                                    </h3>
                                    {statistics?.live_sessions?.length > 5 && (
                                        <a href="/sessions" className="text-xs xs:text-sm text-blue-600 hover:text-blue-800">
                                            View All
                                        </a>
                                    )}
                                </div>
                                
                                {sortedSessions.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <FiVideo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                        <p>No live sessions conducted yet</p>
                                        <p className="text-sm mt-2">Your sessions will appear here once you start conducting them</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 xs:space-y-4">
                                        {sortedSessions.map((session) => (
                                            <motion.div
                                                key={session.id}
                                                variants={cardVariants}
                                                className="p-3 xs:p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2 xs:gap-4">
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-gray-800 text-sm xs:text-base">
                                                            {session.course} - {session.lesson}
                                                        </h4>
                                                        {session?.recording?.url && (
                                                            <a
                                                                href={session?.recording.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 text-xs xs:text-sm flex items-center gap-1 mt-1"
                                                            >
                                                                <FiVideo className="w-3 h-3 xs:w-4 xs:h-4" />
                                                                Recording Link
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="text-xs xs:text-sm text-gray-500 flex items-center gap-2">
                                                        <FiClock className="w-3 h-3 xs:w-4 xs:h-4" />
                                                        <span>
                                                            {formatDate(session.started_at)} |{' '}
                                                            {formatTime(session.started_at)} -{' '}
                                                            {formatTime(session.ended_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DashboardInstructor